import { Check } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Avatar } from '@/shared/ui';
import { playerOf, slotLabel, type BracketMatch, type BracketSlot } from '../model/bracketTypes';
import { CountryFlag } from './CountryFlag';
import { MatchStatusMark } from './MatchStatusMark';

export interface BracketMatchCardProps {
  match: BracketMatch;
  showAvatars?: boolean;
  showFlags?: boolean;
  /** Wywolywane po kliknieciu karty. Bez tego karta nie jest klikalna. */
  onSelect?: (match: BracketMatch) => void;
  className?: string;
}

interface SideProps {
  slot: BracketSlot;
  scores: readonly number[] | undefined;
  /** Wyniki setow przeciwnika — do pogrubienia setow wygranych przez te strone. */
  opponentScores: readonly number[] | undefined;
  tiebreaks: Readonly<Record<number, number>> | undefined;
  isWinner: boolean;
  /** Mecz oddany walkowerem — nie ma setow, wiec przy zwyciezcy pisemy „wo.". */
  walkover: boolean;
  showAvatars: boolean;
  showFlags: boolean;
}

/** Jeden z dwoch wierszy karty meczu. */
function MatchSide({
  slot,
  scores,
  opponentScores,
  tiebreaks,
  isWinner,
  walkover,
  showAvatars,
  showFlags,
}: SideProps) {
  const player = playerOf(slot);
  const label = slotLabel(slot);
  // Para deblowa/mixtowa — każde nazwisko w osobnej linii zamiast długiego wiersza.
  const lines = player ? label.split(' / ') : [label];
  const stacked = lines.length > 1;

  return (
    <div
      className={cn(
        'flex min-w-0 flex-1 items-center gap-2 px-2.5 py-1.5',
        // Delikatne tlo marki wyroznia zwyciezce — jak podswietlone wiersze w makiecie.
        isWinner && 'bg-brand-subtle',
      )}
    >
      <span
        className={cn(
          'w-4 shrink-0 text-right text-[0.6875rem] tabular-nums',
          player?.seed ? 'text-fg-subtle' : 'text-transparent',
        )}
      >
        {player?.seed ?? '0'}
      </span>

      {showFlags && player?.countryCode && <CountryFlag code={player.countryCode} />}

      <span className={cn('flex min-w-0 flex-1 flex-col', stacked && 'gap-0.5')}>
        {lines.map((line, index) => (
          <span key={index} className="flex min-w-0 items-center gap-2">
            {showAvatars && player && (
              <Avatar
                name={line}
                size={stacked ? '2xs' : 'xs'}
                colorful
                {...(!stacked && player.avatarUrl ? { src: player.avatarUrl } : {})}
              />
            )}
            <span
              className={cn(
                'min-w-0 flex-1 truncate text-[0.8125rem]',
                player ? (isWinner ? 'text-fg-strong font-bold' : 'text-fg') : 'text-fg-subtle',
              )}
            >
              {line}
            </span>
          </span>
        ))}
      </span>

      {isWinner && (
        <Check aria-label="Zwycięzca" className="text-success-500 size-3.5 shrink-0" strokeWidth={3} />
      )}

      {walkover && !scores?.length && (
        <span
          className={cn(
            'shrink-0 text-[0.8125rem]',
            isWinner ? 'text-fg-strong font-bold' : 'text-transparent',
          )}
        >
          wo.
        </span>
      )}

      {scores && scores.length > 0 && (
        <span className="flex shrink-0 items-center gap-1.5">
          {scores.map((score, index) => {
            // Pogrubiamy sety wygrane przez TE strone — nie caly wynik zwyciezcy meczu.
            const wonSet = score > (opponentScores?.[index] ?? Number.POSITIVE_INFINITY);
            return (
              <span
                key={index}
                className={cn(
                  'w-3 text-right text-[0.8125rem] tabular-nums',
                  wonSet ? 'text-fg-strong font-bold' : 'text-fg-muted',
                )}
              >
                {score}
                {tiebreaks?.[index] !== undefined && (
                  <sup className="text-fg-subtle ml-px text-[0.5625rem] font-normal">
                    {tiebreaks[index]}
                  </sup>
                )}
              </span>
            );
          })}
        </span>
      )}
    </div>
  );
}

/**
 * Karta pojedynczego meczu w drabince.
 *
 * Wysokosc jest narzucona przez uklad (`matchHeight`), zeby karty i linie
 * laczace zawsze siedzialy na tej samej siatce.
 */
export function BracketMatchCard({
  match,
  showAvatars = false,
  showFlags = false,
  onSelect,
  className,
}: BracketMatchCardProps) {
  const isInteractive = Boolean(onSelect);
  const homeName = playerOf(match.home)?.name ?? 'nieznany';
  const awayName = playerOf(match.away)?.name ?? 'nieznany';

  const content = (
    <>
      <MatchSide
        slot={match.home}
        scores={match.homeScores}
        opponentScores={match.awayScores}
        tiebreaks={match.homeTiebreaks}
        isWinner={match.winner === 'home'}
        walkover={match.status === 'walkover'}
        showAvatars={showAvatars}
        showFlags={showFlags}
      />
      <div className="border-border mx-2.5 border-t" />
      <MatchSide
        slot={match.away}
        scores={match.awayScores}
        opponentScores={match.homeScores}
        tiebreaks={match.awayTiebreaks}
        isWinner={match.winner === 'away'}
        walkover={match.status === 'walkover'}
        showAvatars={showAvatars}
        showFlags={showFlags}
      />

      {/* Tylko „w trakcie”. Rozegrany mecz ma juz zielona fajke przy zwycieskiej
          stronie — drugi znak w rogu karty niosl by te sama informacje. */}
      {match.status === 'live' && (
        <span className="absolute top-1.5 right-1.5 flex items-center">
          <MatchStatusMark status="live" size="sm" />
        </span>
      )}

      {match.scheduleLabel && match.status === 'scheduled' && (
        <span className="bg-surface-sunken text-fg-subtle absolute top-1/2 right-1.5 -translate-y-1/2 rounded-md px-1.5 py-0.5 text-[0.625rem]">
          {match.scheduleLabel}
        </span>
      )}
    </>
  );

  const shared = cn(
    // `overflow-hidden` przycina tlo zwycieskiego wiersza do zaokraglonych rogow.
    'bg-surface border-border relative flex h-full w-full flex-col justify-center overflow-hidden rounded-lg border',
    match.status === 'live' && 'border-brand-300 ring-brand/20 ring-2',
    className,
  );

  if (!isInteractive) {
    return <div className={shared}>{content}</div>;
  }

  return (
    <button
      type="button"
      onClick={() => onSelect?.(match)}
      aria-label={`Mecz: ${homeName} kontra ${awayName}`}
      className={cn(shared, 'hover:border-border-strong text-left transition-colors')}
    >
      {content}
    </button>
  );
}
